import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { StatCard } from './common/StatCard';
import { eduTimetableAPI, eduSchoolsAPI, eduDepartmentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import {
  ArrowLeft, Plus, Upload, Download, Eye, Pencil, Trash2, Loader2, Search,
  BookOpen, DoorOpen, School, Building2, Users, GraduationCap, CheckCircle2,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

// Quote-aware CSV parser -> array of objects keyed by the config's import columns.
// Accepts an optional header row (matched case-insensitively against known columns);
// otherwise assumes values are in the column order given.
const parseCsv = (text, columns) => {
  const lines = (text || '').split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (!lines.length) return [];
  const parseLine = (line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (line[i + 1] === '"') { cur += '"'; i += 1; } else inQuotes = false;
        } else cur += ch;
      } else if (ch === '"') inQuotes = true;
      else if (ch === ',') { out.push(cur); cur = ''; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const firstCells = parseLine(lines[0]).map((h) => h.toLowerCase());
  const hasHeader = firstCells.some((h) => columns.includes(h));
  const keys = hasHeader ? firstCells : columns;
  const start = hasHeader ? 1 : 0;
  const rows = [];
  for (let i = start; i < lines.length; i += 1) {
    const vals = parseLine(lines[i]);
    const obj = {};
    keys.forEach((k, idx) => { if (columns.includes(k)) obj[k] = vals[idx] ?? ''; });
    if (Object.values(obj).some((v) => String(v).trim().length)) rows.push(obj);
  }
  return rows;
};

const downloadCsv = (filename, headerRow, sampleRow) => {
  const csv = `${headerRow.join(',')}\n${sampleRow.join(',')}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const StatusBadge = ({ value }) => {
  const active = value === 'active' || value === true;
  return (
    <Badge variant={active ? 'default' : 'secondary'} className={active ? 'bg-emerald-600 hover:bg-emerald-600' : ''}>
      {active ? 'Active' : 'Inactive'}
    </Badge>
  );
};

/**
 * Full-page catalogue manager. Modes swap the whole page (list → form → view →
 * import), so add / view / edit each open as their own page rather than a dialog.
 *
 * Optional config capabilities (all backward-compatible):
 *  - fields[].type === 'dynamic-select' with `optionsSource()` for async options
 *    (e.g. Departments → School dropdown) and optional `displayKey` for the view.
 *  - `filters`: [{ key, label, optionsSource, allLabel }] rendered above the table;
 *    their values are passed to `api.list(params)`.
 *  - `searchable`: shows a search box whose value is passed as `q` to `api.list`.
 *  - `columns[].status` renders an Active/Inactive badge.
 *  - `stats(rows)`: returns [{ label, value, icon }] KPI cards above the list.
 */
const EducationCatalogPage = ({ config }) => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);
  const Icon = config.icon;
  const dynamicFields = useMemo(() => config.fields.filter((f) => f.type === 'dynamic-select'), [config.fields]);

  const [mode, setMode] = useState('list'); // list | form | view | import
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(config.empty);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({}); // field key -> [{value,label}]

  const [importText, setImportText] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const buildParams = () => {
    const params = {};
    if (config.searchable && search.trim()) params.q = search.trim();
    (config.filters || []).forEach((f) => {
      const v = filterValues[f.key];
      if (v && v !== '__all__') params[f.key] = v;
    });
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      setRows(toArray(await config.api.list(buildParams())));
    } catch (err) {
      if (err?.response?.status === 402) setRows([]);
      else Swal.fire(`Could not load ${config.title.toLowerCase()}`, err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };
  // Reload when search/filters change (and on mount).
  useEffect(() => { load(); }, [search, JSON.stringify(filterValues)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load filter dropdown options once.
  useEffect(() => {
    (config.filters || []).forEach(async (f) => {
      try {
        const opts = await f.optionsSource();
        setFilterOptions((p) => ({ ...p, [f.key]: opts }));
      } catch { /* filter simply stays empty */ }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDynamicOptions = async () => {
    for (const f of dynamicFields) {
      try {
        const opts = await f.optionsSource();
        setDynamicOptions((p) => ({ ...p, [f.key]: opts }));
      } catch { setDynamicOptions((p) => ({ ...p, [f.key]: [] })); }
    }
  };

  const backToList = () => { setMode('list'); setEditingId(null); setCurrent(null); setImportResult(null); setImportText(''); };
  const openNew = async () => { setEditingId(null); setForm(config.empty); await loadDynamicOptions(); setMode('form'); };
  const openEdit = async (row) => { setEditingId(row.id); setForm({ ...config.empty, ...row }); await loadDynamicOptions(); setMode('form'); };
  const openView = (row) => { setCurrent(row); setMode('view'); };
  const openImport = () => { setImportResult(null); setImportText(''); setMode('import'); };
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    for (const f of config.fields) {
      if (f.required && !String(form[f.key] ?? '').trim()) {
        return Swal.fire(`${f.label} required`, `Please enter ${f.label.toLowerCase()}.`, 'warning');
      }
    }
    const payload = { ...form };
    (config.numberFields || []).forEach((k) => {
      if (payload[k] === '' || payload[k] === null || payload[k] === undefined) payload[k] = null;
      else payload[k] = Number(payload[k]) || 0;
    });
    setSaving(true);
    try {
      if (editingId) await config.api.update(editingId, payload);
      else await config.api.create(payload);
      await load();
      backToList();
      Swal.fire({ icon: 'success', title: editingId ? `${config.entityLabel} updated` : `${config.entityLabel} added`, timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: `Delete ${config.entityLabel.toLowerCase()}?`,
      text: row[config.primaryKey], showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try {
      await config.api.remove(row.id);
      await load();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1100, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportText(String(ev.target.result || ''));
    reader.readAsText(file);
  };

  const runImport = async () => {
    const parsed = parseCsv(importText, config.importColumns);
    if (!parsed.length) return Swal.fire('Nothing to import', 'Paste CSV rows or upload a .csv file first.', 'warning');
    const items = parsed.map((r) => {
      const item = { ...r };
      (config.numberFields || []).forEach((k) => { if (item[k] !== undefined && item[k] !== '') item[k] = Number(item[k]) || 0; });
      return item;
    });
    setImporting(true);
    try {
      const res = await config.api.importItems(items);
      setImportResult(res.data);
      await load();
    } catch (err) {
      Swal.fire('Import failed', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setImporting(false);
    }
  };

  const stats = config.stats ? config.stats(rows) : null;

  const renderField = (f) => {
    if (f.type === 'dynamic-select') {
      const opts = dynamicOptions[f.key] || [];
      return (
        <Select value={form[f.key] ? String(form[f.key]) : ''} onValueChange={(v) => setField(f.key, v)}>
          <SelectTrigger><SelectValue placeholder={opts.length ? `Select ${f.label.toLowerCase()}` : `No ${f.label.toLowerCase()} available`} /></SelectTrigger>
          <SelectContent>
            {opts.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === 'select') {
      return (
        <Select value={form[f.key] || ''} onValueChange={(v) => setField(f.key, v)}>
          <SelectTrigger><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
          <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === 'textarea') {
      return <Textarea rows={3} value={form[f.key] ?? ''} placeholder={f.placeholder || ''} onChange={(e) => setField(f.key, e.target.value)} />;
    }
    return (
      <Input
        type={f.type === 'number' ? 'number' : 'text'}
        value={form[f.key] ?? ''}
        placeholder={f.placeholder || ''}
        onChange={(e) => setField(f.key, e.target.value)}
      />
    );
  };

  const renderCell = (row, c) => {
    if (c.status) return <StatusBadge value={row[c.key]} />;
    if (c.badge) return row[c.key] ? <Badge variant="secondary">{row[c.key]}</Badge> : '—';
    const v = row[c.key];
    return v === '' || v === null || v === undefined ? '—' : v;
  };

  const headerBar = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {mode !== 'list' && (
          <Button variant="ghost" size="icon" onClick={backToList} title="Back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Icon className="h-6 w-6 text-indigo-600" />
            {mode === 'list' && config.title}
            {mode === 'form' && (editingId ? `Edit ${config.entityLabel}` : `New ${config.entityLabel}`)}
            {mode === 'view' && `${config.entityLabel} Details`}
            {mode === 'import' && `Import ${config.title}`}
          </h2>
          {mode === 'list' && <p className="text-sm text-muted-foreground">{config.subtitle}</p>}
        </div>
      </div>
      {mode === 'list' && canManage && (
        <div className="flex items-center gap-2">
          {config.api.exportUrl && (
            <Button variant="outline" onClick={() => config.api.exportUrl()}><Download className="h-4 w-4 mr-1" /> Export</Button>
          )}
          <Button variant="outline" onClick={openImport}><Upload className="h-4 w-4 mr-1" /> Import</Button>
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" /> New {config.entityLabel}</Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {headerBar}

      {mode === 'list' && (
        <>
          {stats && stats.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((s) => <StatCard key={s.label} label={s.label} value={s.value} icon={s.icon} />)}
            </div>
          )}

          {(config.searchable || (config.filters && config.filters.length > 0)) && (
            <div className="flex flex-wrap items-center gap-3">
              {config.searchable && (
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-8 w-64" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
              )}
              {(config.filters || []).map((f) => (
                <Select key={f.key} value={filterValues[f.key] || '__all__'} onValueChange={(v) => setFilterValues((p) => ({ ...p, [f.key]: v }))}>
                  <SelectTrigger className="w-56"><SelectValue placeholder={f.label} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{f.allLabel || `All ${f.label.toLowerCase()}`}</SelectItem>
                    {(filterOptions[f.key] || []).map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ))}
            </div>
          )}

          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
              ) : rows.length === 0 ? (
                <EmptyState icon={Icon} title={`No ${config.title.toLowerCase()} yet`}
                  description={canManage ? `Add or import ${config.title.toLowerCase()} to get started.` : `No ${config.title.toLowerCase()} have been added yet.`} />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {config.columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.id}>
                          {config.columns.map((c) => (
                            <TableCell key={c.key} className={c.primary ? 'font-medium' : ''}>
                              {renderCell(row, c)}
                            </TableCell>
                          ))}
                          <TableCell className="text-right whitespace-nowrap">
                            <Button variant="ghost" size="icon" onClick={() => openView(row)} title="View"><Eye className="h-4 w-4" /></Button>
                            {canManage && <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                            {canManage && <Button variant="ghost" size="icon" onClick={() => remove(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {mode === 'form' && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? `Edit ${config.entityLabel}` : `New ${config.entityLabel}`}</CardTitle>
            <CardDescription>Fields marked * are required.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 max-w-3xl">
              {config.fields.map((f) => (
                <div key={f.key} className={`space-y-1 ${f.full ? 'md:col-span-2' : ''}`}>
                  <Label>{f.label}{f.required ? ' *' : ''}</Label>
                  {renderField(f)}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6 max-w-3xl">
              <Button variant="outline" onClick={backToList} disabled={saving}>Cancel</Button>
              <Button onClick={submit} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {editingId ? 'Save changes' : `Create ${config.entityLabel.toLowerCase()}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === 'view' && current && (
        <Card>
          <CardHeader><CardTitle>{current[config.primaryKey]}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 max-w-3xl">
              {config.fields.map((f) => {
                let display = current[f.key];
                if (f.type === 'dynamic-select' && f.displayKey) display = current[f.displayKey];
                return (
                  <div key={f.key} className={f.full ? 'md:col-span-2' : ''}>
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{f.label}</div>
                    <div className="text-sm mt-0.5">
                      {f.type === 'select' && current[f.key]
                        ? <Badge variant="secondary">{current[f.key]}</Badge>
                        : (display === '' || display === null || display === undefined ? '—' : String(display))}
                    </div>
                  </div>
                );
              })}
              {(config.viewExtras || []).map((x) => (
                <div key={x.key}>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{x.label}</div>
                  <div className="text-sm mt-0.5">{current[x.key] ?? 0}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6 max-w-3xl">
              <Button variant="outline" onClick={backToList}>Back</Button>
              {canManage && <Button onClick={() => openEdit(current)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>}
            </div>
          </CardContent>
        </Card>
      )}

      {mode === 'import' && (
        <Card>
          <CardHeader>
            <CardTitle>Import {config.title}</CardTitle>
            <CardDescription>
              Upload a .csv file or paste rows. Columns: <code className="text-xs">{config.importColumns.join(', ')}</code>.
              Duplicate {config.primaryKey}s are skipped; invalid rows are reported.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => downloadCsv(`${config.entity}-template.csv`, config.importColumns, config.importSample)}>
                <Download className="h-4 w-4 mr-1" /> Download template
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
                <span className="inline-flex items-center rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                  <Upload className="h-4 w-4 mr-1" /> Upload .csv
                </span>
              </label>
            </div>
            <div className="space-y-1">
              <Label>CSV rows</Label>
              <Textarea
                rows={8}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={`${config.importColumns.join(',')}\n${config.importSample.join(',')}`}
                className="font-mono text-xs"
              />
            </div>
            {importResult && (
              <div className="rounded-lg border p-4 text-sm space-y-2">
                <div className="flex flex-wrap gap-4">
                  <span>Total: <b>{importResult.total}</b></span>
                  <span className="text-emerald-600">Created: <b>{importResult.created}</b></span>
                  <span className="text-amber-600">Skipped (duplicates): <b>{importResult.skipped}</b></span>
                  <span className="text-rose-600">Errors: <b>{importResult.errors?.length || 0}</b></span>
                </div>
                {importResult.errors?.length > 0 && (
                  <ul className="list-disc pl-5 text-rose-600">
                    {importResult.errors.slice(0, 10).map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
                    {importResult.errors.length > 10 && <li>…and {importResult.errors.length - 10} more</li>}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={backToList}>Done</Button>
              <Button onClick={runImport} disabled={importing}>
                {importing && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Import rows
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ------------------------------ helpers ------------------------------
const triggerDownload = async (promise, filename) => {
  try {
    const res = await promise;
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    Swal.fire('Export failed', err?.response?.data?.detail || 'Please try again.', 'error');
  }
};

const activeSchoolOptions = async () => {
  const res = await eduSchoolsAPI.list({ status: 'active' });
  return toArray(res).map((s) => ({ value: s.id, label: `${s.name} (${s.code})` }));
};

// ------------------------------ configs ------------------------------
const COURSES_CONFIG = {
  entity: 'course',
  entityLabel: 'Course',
  title: 'Courses',
  subtitle: 'The course catalogue for this institute.',
  primaryKey: 'code',
  icon: BookOpen,
  api: {
    list: (params) => eduTimetableAPI.listCourses(params),
    create: (d) => eduTimetableAPI.createCourse(d),
    update: (id, d) => eduTimetableAPI.updateCourse(id, d),
    remove: (id) => eduTimetableAPI.deleteCourse(id),
    importItems: (items) => eduTimetableAPI.importCourses(items),
  },
  columns: [
    { key: 'code', label: 'Code', primary: true },
    { key: 'title', label: 'Title' },
    { key: 'department', label: 'Department' },
    { key: 'course_type', label: 'Type', badge: true },
    { key: 'credits', label: 'Credits' },
    { key: 'weekly_hours', label: 'Weekly Hrs' },
  ],
  fields: [
    { key: 'code', label: 'Code', required: true, placeholder: 'CS-201' },
    { key: 'title', label: 'Title', required: true, placeholder: 'Data Structures' },
    { key: 'department', label: 'Department', placeholder: 'Computer Science' },
    { key: 'course_type', label: 'Course Type', type: 'select', options: ['lecture', 'lab', 'practical'] },
    { key: 'credits', label: 'Credits', type: 'number' },
    { key: 'weekly_hours', label: 'Weekly Hours', type: 'number' },
  ],
  empty: { code: '', title: '', department: '', course_type: 'lecture', credits: 0, weekly_hours: 0 },
  numberFields: ['credits', 'weekly_hours'],
  importColumns: ['code', 'title', 'department', 'course_type', 'credits', 'weekly_hours'],
  importSample: ['CS-201', 'Data Structures', 'Computer Science', 'lecture', '4', '3'],
};

const ROOMS_CONFIG = {
  entity: 'room',
  entityLabel: 'Room',
  title: 'Rooms',
  subtitle: 'Classrooms and labs available for scheduling.',
  primaryKey: 'name',
  icon: DoorOpen,
  api: {
    list: (params) => eduTimetableAPI.listRooms(params),
    create: (d) => eduTimetableAPI.createRoom(d),
    update: (id, d) => eduTimetableAPI.updateRoom(id, d),
    remove: (id) => eduTimetableAPI.deleteRoom(id),
    importItems: (items) => eduTimetableAPI.importRooms(items),
  },
  columns: [
    { key: 'name', label: 'Name', primary: true },
    { key: 'room_type', label: 'Type', badge: true },
    { key: 'capacity', label: 'Capacity' },
    { key: 'building', label: 'Building' },
  ],
  fields: [
    { key: 'name', label: 'Name', required: true, placeholder: 'Room A-101' },
    { key: 'room_type', label: 'Room Type', type: 'select', options: ['classroom', 'lab'] },
    { key: 'capacity', label: 'Capacity', type: 'number' },
    { key: 'building', label: 'Building', placeholder: 'Block A' },
  ],
  empty: { name: '', room_type: 'classroom', capacity: 0, building: '' },
  numberFields: ['capacity'],
  importColumns: ['name', 'room_type', 'capacity', 'building'],
  importSample: ['Room A-101', 'classroom', '60', 'Block A'],
};

const SCHOOLS_CONFIG = {
  entity: 'school',
  entityLabel: 'School',
  title: 'Schools',
  subtitle: 'Top-level academic units. Departments and faculty belong to a school.',
  primaryKey: 'name',
  icon: School,
  searchable: true,
  api: {
    list: (params) => eduSchoolsAPI.list(params),
    create: (d) => eduSchoolsAPI.create(d),
    update: (id, d) => eduSchoolsAPI.update(id, d),
    remove: (id) => eduSchoolsAPI.remove(id),
    importItems: (items) => eduSchoolsAPI.importItems(items),
    exportUrl: () => triggerDownload(eduSchoolsAPI.exportCsv(), 'schools.csv'),
  },
  stats: (rows) => [
    { label: 'Total Schools', value: rows.length, icon: School },
    { label: 'Active', value: rows.filter((r) => r.is_active).length, icon: CheckCircle2 },
    { label: 'Departments', value: rows.reduce((n, r) => n + (r.department_count || 0), 0), icon: Building2 },
    { label: 'Faculty', value: rows.reduce((n, r) => n + (r.faculty_count || 0), 0), icon: Users },
  ],
  columns: [
    { key: 'code', label: 'Code', primary: true },
    { key: 'name', label: 'Name' },
    { key: 'dean_name', label: 'Dean' },
    { key: 'department_count', label: 'Depts' },
    { key: 'faculty_count', label: 'Faculty' },
    { key: 'status', label: 'Status', status: true },
  ],
  fields: [
    { key: 'code', label: 'Code', required: true, placeholder: 'SOE' },
    { key: 'name', label: 'Name', required: true, placeholder: 'School of Engineering' },
    { key: 'short_name', label: 'Short Name', placeholder: 'Engg' },
    { key: 'dean_name', label: 'Dean', placeholder: 'Dr. A. Sharma' },
    { key: 'email', label: 'Email', placeholder: 'soe@institute.edu' },
    { key: 'phone', label: 'Phone' },
    { key: 'website', label: 'Website', placeholder: 'https://…' },
    { key: 'display_order', label: 'Display Order', type: 'number' },
    { key: 'address', label: 'Address', type: 'textarea', full: true },
    { key: 'description', label: 'Description', type: 'textarea', full: true },
  ],
  empty: {
    code: '', name: '', short_name: '', dean_name: '', email: '', phone: '',
    website: '', display_order: 0, address: '', description: '', is_active: true,
  },
  numberFields: ['display_order'],
  viewExtras: [
    { key: 'department_count', label: 'Departments' },
    { key: 'faculty_count', label: 'Faculty' },
  ],
  importColumns: ['code', 'name', 'short_name', 'dean_name', 'email', 'phone', 'is_active'],
  importSample: ['SOE', 'School of Engineering', 'Engg', 'Dr. A. Sharma', 'soe@institute.edu', '', 'active'],
};

const DEPARTMENTS_CONFIG = {
  entity: 'department',
  entityLabel: 'Department',
  title: 'Departments',
  subtitle: 'Academic departments. Each belongs to a school; faculty belong to a department.',
  primaryKey: 'name',
  icon: Building2,
  searchable: true,
  filters: [
    { key: 'school_id', label: 'School', allLabel: 'All schools', optionsSource: activeSchoolOptions },
  ],
  api: {
    list: (params) => eduDepartmentsAPI.list(params),
    create: (d) => eduDepartmentsAPI.create(d),
    update: (id, d) => eduDepartmentsAPI.update(id, d),
    remove: (id) => eduDepartmentsAPI.remove(id),
    importItems: (items) => eduDepartmentsAPI.importItems(items),
    exportUrl: () => triggerDownload(eduDepartmentsAPI.exportCsv(), 'departments.csv'),
  },
  stats: (rows) => [
    { label: 'Total Departments', value: rows.length, icon: Building2 },
    { label: 'Active', value: rows.filter((r) => r.is_active).length, icon: CheckCircle2 },
    { label: 'Faculty', value: rows.reduce((n, r) => n + (r.faculty_count || 0), 0), icon: GraduationCap },
  ],
  columns: [
    { key: 'school_name', label: 'School', primary: true },
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Department' },
    { key: 'hod_name', label: 'HOD' },
    { key: 'faculty_count', label: 'Faculty' },
    { key: 'status', label: 'Status', status: true },
  ],
  fields: [
    { key: 'school_id', label: 'School', type: 'dynamic-select', required: true, displayKey: 'school_name', optionsSource: activeSchoolOptions },
    { key: 'code', label: 'Code', required: true, placeholder: 'CSE' },
    { key: 'name', label: 'Name', required: true, placeholder: 'Computer Science' },
    { key: 'short_name', label: 'Short Name', placeholder: 'CS' },
    { key: 'hod_name', label: 'Head of Department', placeholder: 'Dr. R. Verma' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'display_order', label: 'Display Order', type: 'number' },
    { key: 'description', label: 'Description', type: 'textarea', full: true },
  ],
  empty: {
    school_id: '', code: '', name: '', short_name: '', hod_name: '', email: '',
    phone: '', display_order: 0, description: '', is_active: true,
  },
  numberFields: ['school_id', 'display_order'],
  viewExtras: [{ key: 'faculty_count', label: 'Faculty' }],
  importColumns: ['school_code', 'code', 'name', 'short_name', 'hod_name', 'email', 'phone', 'is_active'],
  importSample: ['SOE', 'CSE', 'Computer Science', 'CS', 'Dr. R. Verma', 'cse@institute.edu', '', 'active'],
};

export const EducationCoursesPage = () => <EducationCatalogPage config={COURSES_CONFIG} />;
export const EducationRoomsPage = () => <EducationCatalogPage config={ROOMS_CONFIG} />;
export const EducationSchoolsPage = () => <EducationCatalogPage config={SCHOOLS_CONFIG} />;
export const EducationDepartmentsPage = () => <EducationCatalogPage config={DEPARTMENTS_CONFIG} />;

export default EducationCatalogPage;
